<script setup lang="ts">
import { ElPopover, ElInput, ElCollapse, ElCollapseItem } from "element-plus";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { LocationCategory, type LocationGroup } from "@/api/locationGroups";
import { useLocationSelector } from "@/composables/location";
import { useLocationStore } from "@/stores/location";
import { useAuthStore } from "@/stores/auth";
import { useRoute } from "vue-router";
import { config } from "@/config/envConfig";

import type { Location } from "@/api/locations";

export interface Props {
  visible?: boolean;
}

withDefaults(defineProps<Props>(), {
  visible: true,
});

const SHOW_MANAGE_GROUPS_LINKS = config.SHOW_MANAGE_GROUPS_LINKS;

const { t } = useI18n();
const authStore = useAuthStore();
const locationStore = useLocationStore();

const LOCATIONS_COUNT_REQUIRED_FOR_SEARCH = 10;

const popoverVisible = ref(false);
const searchTerm = ref("");
const expandedGroupPk = ref("");

const {
  locationGroups,
  selectedLocation,
  selectedLocationPk,
  selectedLocationGroup,
  selectedLocationGroupPk,
  isAllLocationsSelected,
  locationLocalStorage,
  isLocationGroupsDataLoaded,
  selectLocation,
  getLocationOrGroupByPk,
  isLocationGroup,
} = useLocationSelector();

const groupItems = computed<LocationGroup[]>(() => {
  const filter = searchTerm.value.toLowerCase();
  const groups = locationGroups.value.filter(
    (group: LocationGroup) =>
      !group.invisible &&
      group.locations.length > 0 &&
      (group.name?.toLowerCase().includes(filter) ||
        group.locations.some(
          (location: Location) => location.name?.toLowerCase().includes(filter)
        ))
  );
  const groupsWithFilteredChildren = groups
    .map((group) => ({
      ...group,
      locations: group.locations
        .filter((location) => location.name.toLowerCase().includes(filter))
        .sort((l1, l2) => l1.name.localeCompare(l2.name)),
    }))
    .sort((lg1, lg2) => lg1.name.localeCompare(lg2.name));
  return groupsWithFilteredChildren;
});

const singleLocations = computed(() => {
  const items: { group: LocationGroup; location: Location }[] = [];
  locationGroups.value.forEach((group: LocationGroup) => {
    group.locations.forEach((location: Location) => {
      if (!group.invisible) {
        return;
      }

      if (
        !searchTerm.value ||
        location.name?.toLowerCase().includes(searchTerm.value.toLowerCase())
      ) {
        items.push({ group, location });
      }
    });
  });
  return items.sort((l1, l2) =>
    l1.location.name.localeCompare(l2.location.name)
  );
});

const showSearchBar = computed(() => {
  return (
    locationGroups.value.reduce((acc: Location[], cur: LocationGroup) => {
      acc.push(...cur.locations);
      return acc;
    }, []).length >= LOCATIONS_COUNT_REQUIRED_FOR_SEARCH
  );
});

const route = useRoute();

const resetPopover = () => {
  popoverVisible.value = false;
  searchTerm.value = "";
  expandedGroupPk.value = "";
};

watch(
  () => route.meta,
  () => {
    resetPopover();
    if (isLocationChangeNeeded.value && allowedLocationOrGroup.value) {
      selectLocation(allowedLocationOrGroup.value);
    }
  }
);

const changeLocation = (pk?: string) => {
  resetPopover();

  if (!pk) {
    selectLocation();
  } else {
    selectLocation(pk);
    expandedGroupPk.value =
      (selectedLocationPk.value && selectedLocationGroup.value?.id) || "";
  }
};

const allLocationsDisabled = computed(() => !!route.meta?.allLocationsDisabled);
const singleLocationsDisabled = computed(
  () => !!route.meta?.singleLocationsDisabled
);
const groupLocationsDisabled = computed(
  () => !!route.meta?.groupLocationsDisabled
);

const needToChangeFromAllLocations = computed(
  () => allLocationsDisabled.value && isAllLocationsSelected.value
);

const needToChangeFromSingleLocations = computed(
  () => singleLocationsDisabled.value
);

const isLocationChangeNeeded = computed(
  () =>
    needToChangeFromAllLocations.value || needToChangeFromSingleLocations.value
);

const selectedLocationType = computed(() => locationStore.selectedLocationType);

const allowedLocationOrGroup = computed(() => {
  if (needToChangeFromAllLocations.value) {
    return groupLocationsDisabled.value
      ? selectedLocation.value.id
      : selectedLocationGroup.value?.id;
  }
  if (needToChangeFromSingleLocations.value) {
    return selectedLocationGroup.value?.id;
  }
  return null;
});

const selectorNameValue = computed(() => {
  let name = "";
  if (isAllLocationsSelected.value) {
    name = t("locationSelector.allLocations");
  }
  if (selectedLocationType.value === LocationCategory.GROUP) {
    name = selectedLocationGroup.value?.name ?? "";
  }
  if (selectedLocationType.value === LocationCategory.SINGLE) {
    name = selectedLocation.value.name ?? "";
  }

  return name;
});

const isSettingsPage = computed(() => route.path.includes("settings"));

watch(
  isLocationGroupsDataLoaded,
  (_newVal, oldVal) => {
    if (oldVal || !authStore.authUser) return;

    const locationDataCached =
      locationLocalStorage.value.location &&
      locationLocalStorage.value.company === authStore.authUser.companyPk;

    const selectAllLocations =
      locationLocalStorage.value.allLocations && !allLocationsDisabled.value;

    if (locationDataCached && selectAllLocations) {
      selectLocation();
      return;
    }

    const defaultLocation =
      getLocationOrGroupByPk(
        locationLocalStorage.value.location ?? undefined
      ) ||
      getLocationOrGroupByPk(authStore.authUser.locationPk) ||
      locationGroups.value[0];

    if (!defaultLocation) {
      return;
    }

    let activeLocation = defaultLocation?.id;
    if (!isLocationGroup(defaultLocation) && singleLocationsDisabled.value) {
      activeLocation = (defaultLocation as Location).locationGroupPk ?? "";
    }
    if (isLocationGroup(defaultLocation) && groupLocationsDisabled.value) {
      activeLocation = (defaultLocation as LocationGroup).locations?.[0]?.id;
    }
    selectLocation(activeLocation ?? undefined);

    expandedGroupPk.value =
      (selectedLocationPk.value && selectedLocationGroupPk.value) || "";
  },
  { immediate: true }
);

const showLocationLabel = computed(() => {
  const hasGroupAndNoLocations = locationGroups.value
    .filter((location) => !location.invisible)
    .every((item) => !item.locations.length);

  const hasOnlyOneLocation =
    locationGroups.value.filter((location) => location.invisible).length === 1;

  if (
    locationGroups.value.length > 1 &&
    hasGroupAndNoLocations &&
    hasOnlyOneLocation
  )
    return true;

  if (locationGroups.value.length === 1 && locationGroups.value[0].invisible)
    return true;

  return false;
});
</script>

<template>
  <div
    v-if="visible"
    v-loading="!selectorNameValue"
    :class="[
      'location-selector',
      {
        'ellipsis-location': showLocationLabel,
      },
    ]"
  >
    <el-popover
      v-if="!showLocationLabel"
      v-model:visible="popoverVisible"
      placement="bottom-start"
      :width="280"
      :show-arrow="false"
      :append-to-body="false"
      :teleported="false"
      trigger="click"
    >
      <div
        v-if="showSearchBar"
        class="search-bar"
      >
        <el-input
          v-model="searchTerm"
          :placeholder="t('locationSelector.enterLocation')"
        >
          <template #prefix>
            <font-awesome-icon :icon="['fal', 'magnifying-glass']" />
          </template>
        </el-input>
      </div>
      <div class="locations-list">
        <ul
          v-if="locationGroups.length > 1"
          class="section"
        >
          <li
            :class="[
              'option all-locations',
              {
                active: isAllLocationsSelected,
                disabled: allLocationsDisabled,
              },
            ]"
            @click="() => (allLocationsDisabled ? null : changeLocation())"
            @keyup.enter="
              () => (allLocationsDisabled ? null : changeLocation())
            "
          >
            {{ t("locationSelector.allLocations") }}
          </li>
        </ul>
        <el-collapse
          v-if="groupItems.length"
          v-model="expandedGroupPk"
          class="group-list"
          accordion
        >
          <el-collapse-item
            v-for="group in groupItems"
            :key="group.id"
            :name="group.id"
          >
            <template #title>
              <div
                :class="[
                  {
                    'group-title-active':
                      group.id === selectedLocationGroupPk &&
                      !selectedLocationPk,
                  },
                ]"
                @click="
                  () =>
                    groupLocationsDisabled ? null : changeLocation(group.id)
                "
                @keyup.enter="
                  () =>
                    groupLocationsDisabled ? null : changeLocation(group.id)
                "
              >
                {{ group.name }}
              </div>
            </template>
            <li
              v-for="location in group.locations"
              :key="location.id"
              :class="[
                'option',
                {
                  disabled: singleLocationsDisabled,
                  active: location.id === selectedLocationPk,
                },
              ]"
              @click="
                () =>
                  singleLocationsDisabled ? null : changeLocation(location.id)
              "
              @keyup.enter="
                () =>
                  singleLocationsDisabled ? null : changeLocation(location.id)
              "
            >
              {{ location.name }}
            </li>
            <li
              v-if="SHOW_MANAGE_GROUPS_LINKS.groups"
              class="option manage-groups"
            >
              <font-awesome-icon
                class="icon"
                :icon="['fal', 'gear']"
              />
              <!-- Hook up @click event later when locations page exists in Blue Steel -->
              <p>{{ t("locationSelector.manageGroup") }}</p>
            </li>
          </el-collapse-item>
        </el-collapse>
        <ul
          v-if="singleLocations.length"
          class="section"
        >
          <li
            v-for="item in singleLocations"
            :key="item.location.id"
            :class="[
              'option parent',
              { active: item.group.id === selectedLocationGroupPk },
            ]"
            @click="() => changeLocation(item.group.id)"
            @keyup.enter="() => changeLocation(item.group.id)"
          >
            {{ item.location.name }}
          </li>
        </ul>
      </div>

      <div
        v-if="SHOW_MANAGE_GROUPS_LINKS.all"
        class="option manage manage-groups"
      >
        <!-- Hook up @click event later when locations page exists in Blue Steel -->
        <font-awesome-icon
          class="icon"
          :icon="['fal', 'gear']"
        />
        <p>{{ t("locationSelector.manageLocationsAndGroups") }}</p>
      </div>

      <template #reference>
        <div class="popover-reference">
          <h2
            v-if="isSettingsPage"
            v-cy="`locationSelector-${selectorNameValue}`"
            :class="['location-name', { 'location-name-h2': isSettingsPage }]"
          >
            {{ selectorNameValue }}
          </h2>
          <h1
            v-else
            v-cy="`locationSelector-${selectorNameValue}`"
            class="location-name"
          >
            {{ selectorNameValue }}
          </h1>
          <font-awesome-icon
            :class="['chevron-down', { 'chevron-down-h2': isSettingsPage }]"
            :icon="['far', 'chevron-down']"
          />
        </div>
      </template>
    </el-popover>

    <div
      v-else
      :class="['location-label', { 'location-label-setting': isSettingsPage }]"
    >
      {{ selectorNameValue }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
ul,
li {
  list-style: none;
}

.location-selector {
  display: flex;
  width: 100%;
  font-family: var(--font-family-base);
  font-weight: var(--font-normal);
}

.popover-reference {
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
}

.manage {
  margin-top: 8px;
  padding: 10px 20px;
  background: var(--so-bg-color-page);
}

.manage span {
  font-weight: var(--font-normal);
  font-size: 0.8125rem;
  color: var(--so-text-color-primary);
  transition: var(--so-transition-color);
  cursor: pointer;

  &:hover {
    color: var(--so-color-secondary);
  }
}

.location-name {
  font-size: 2rem;
  font-weight: var(--font-normal);
  line-height: 1.5;
  margin-left: 5px;
  transition: 0.3s;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: calc(100% - 4px);
    width: 100%;
    height: 1px;
    background-color: var(--so-text-color-primary);
    transition: 0.3s;
  }

  &:hover {
    color: var(--so-color-secondary);

    &::before {
      background-color: var(--so-color-secondary);
    }
  }

  @media screen and (max-width: $breakpoint-sm) {
    font-size: 1.4375rem;
    line-height: 1.6;
    margin-left: 0;
    max-width: calc(100vw - 120px);
  }
}

.location-name-h2 {
  font-size: 1.4375rem;
  line-height: 1.6;
}

.chevron-down {
  margin: 5px 0 0 7px;
}

.chevron-down-h2 {
  margin: 1px 0 0 7px;
}

.location-name + .chevron-down {
  transition: 0.3s;
}

.location-name:hover + .chevron-down {
  color: var(--so-color-secondary);
}

:deep(.so-popover.so-popper) {
  padding: 0;
  border: 0;
}

.search-bar {
  padding: 13px 20px 0;
}

.search-bar .so-input {
  height: 40px;
}

.locations-list {
  max-height: 460px;
  overflow-y: auto;
  padding: 2px 20px 0;
}

.section {
  padding: 8px 0;
}

.section + .section {
  border-top: 1px solid var(--so-border-color);
}

.section .option.parent:last-child {
  margin-bottom: 0;
}

.option {
  color: var(--so-text-color-primary);
  cursor: pointer;
  font-size: 0.8125rem;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:not(.parent) {
    margin-bottom: 0px;
    font-weight: normal;
  }

  &:hover,
  &.focus,
  &.active {
    color: var(--so-color-secondary);
  }

  &.disabled {
    color: var(--so-text-color-disabled);
    cursor: not-allowed;
  }
}

.option.all-locations.disabled {
  color: var(--so-text-color-primary);
  margin-left: 0;
}

.manage-groups {
  display: flex;
  align-items: center;
  margin-top: 4px;

  p,
  .icon {
    transition: 0.2s;
  }

  p {
    font-weight: var(--font-normal);
    font-size: 0.8125rem;
    margin-left: 4px;
  }
}

.manage-groups:hover {
  p,
  .icon {
    color: var(--so-color-secondary);
  }
}
:deep(.so-collapse) {
  --so-collapse-header-bg-color: transparent;
}
:deep(.so-collapse-item__wrap) {
  --so-collapse-content-bg-color: transparent;
}
.group-list {
  --so-collapse-header-height: 40px;
}

.group-list :deep(.so-collapse-item__header) {
  font-weight: normal;
  justify-content: space-between;
}

.group-list :deep(.so-collapse-item__header div) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-list :deep(.so-collapse-item__header.focusing),
.group-list :deep(.so-collapse-item__header:hover),
.group-title-active {
  color: var(--so-color-secondary);
}

.group-list :deep(.so-collapse-item__arrow) {
  transform: rotate(90deg);
  margin-left: 8px;
  min-width: 13px;
}

.group-list :deep(.so-collapse-item__arrow.is-active) {
  transform: rotate(270deg);
}

.group-list :deep(.so-collapse-item__content) {
  margin-top: -5px;
  padding: 0px 8px 10px;
}

.ellipsis-location {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.location-label {
  font-size: 2rem;
  line-height: 1.5;
  font-weight: var(--font-normal);
  font-family: var(--font-family-headings);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-left: 3px;
  min-width: 0;
  max-width: 100%;

  @media screen and (max-width: $breakpoint-sm) {
    font-size: 1.4375rem;
    line-height: 1.6;
    margin-left: 0;
  }
}

.location-label-setting {
  line-height: 1.6;
  font-size: 1.4375rem;
  margin-left: 5px;

  @media screen and (max-width: $breakpoint-sm) {
    margin-left: 0;
  }
}
</style>
